import { AlignmentPeriodCalendar } from '@vendasta/multi-location-analytics';
import { calculateStartDate, DateDuration } from '@vendasta/galaxy/utility/date-utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Duration from 'dayjs/plugin/duration';

dayjs.extend(Duration);
dayjs.extend(utc);

export type FormattingOptions = 'month-long' | 'month' | 'week' | 'week-long' | 'full-date' | 'day' | 'none';

export const granularityThresholdForNumberOfDays = 89;

// Granularity is an enumeration of the ways time data may be bucketed by
export enum Granularity {
  Month = 'month',
  Week = 'week', // Weeks beginning on Monday
  Day = 'day',
  // More units are available -- these line up with use by dayjs-range and SQL
}

// Granularity days for marketing funnel metrics
export const granularityFunnelMetricsWeekly = 89;
export const granularityFunnelMetricsMonthly = 183;

// Takes a preset time range and returns the date boundaries intended for display to user.
export function getDateBoundaries(timeRange: string): [dayjs.Dayjs, dayjs.Dayjs] {
  const endMoment = dayjs().startOf('day');
  let startMoment: dayjs.Dayjs;
  const dayOffsetMap = {
    '7days': {
      val: 7,
      type: 'days',
    },
    '30days': {
      val: 30,
      type: 'days',
    },
    '90days': {
      val: 90,
      type: 'days',
    },
    '6months': {
      val: 6,
      type: 'months',
    },
    '12months': {
      val: 12,
      type: 'months',
    },
  };
  // A single day's stats would read the same start end
  if (dayOffsetMap[timeRange].type === 'months') {
    /* For 6 months and 12 months, the date should start with 1 day late for included day */
    const calculatedStart = calculateStartDate(endMoment.toDate(), dayOffsetMap[timeRange].val, DateDuration.months);
    startMoment = dayjs(calculatedStart.setDate(calculatedStart.getDate() + 1));
  } else {
    startMoment = dayjs(calculateStartDate(endMoment.toDate(), dayOffsetMap[timeRange].val - 1, DateDuration.days));
  }
  return [startMoment, endMoment];
}

// alignUTCDateToLocal is a helper intended to make UTC dates aligned to month/week/day starts to a local date
// with such an alignment. *This shifts the dayjs in time*, but makes display easier.
export function alignUTCDateToLocal(now: Date): Date {
  return new Date(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds(),
  );
}

// alignLocalDateToUTC is a helper intended to make local dates aligned to month/week/day starts to a UTC date
// with such an alignment. *This shifts the dayjs in time*,
// but masks the inability for us to answer timezone specific queries at the dayjs.
export function alignLocalDateToUTC(now: Date): Date {
  if (!now) {
    return now;
  }
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
    ),
  );
}

// Aligns the date passed in to the end of the day (11:59:59 PM)
export function alignToEndOfDay(date: Date): Date {
  return dayjs(date).endOf('day').toDate();
}

// Aligns the date passed in to the start of the day (00:00:00 AM)
export function alignToStartOfDay(date: Date): Date {
  return dayjs(date).startOf('day').toDate();
}

// Aligns the dates passed in to the start and end respectively
export function alignStartAndEndDates(startDate: Date, endDate: Date): [Date, Date] {
  return [alignToStartOfDay(startDate), alignToEndOfDay(endDate)];
}

// Generates Dates with by iterating through range by units
export function generateDates(start: Date, end: Date, granularity: Granularity): Date[] {
  const range = [];
  const startDate = new Date(start);
  const endDate = new Date(end);
  switch (granularity) {
    case Granularity.Day: {
      startDate.setDate(startDate.getDate());
      break;
    }
    case Granularity.Week: {
      startDate.setDate(startDate.getDate() - startDate.getDay() + 1);
      endDate.setDate(endDate.getDate() - endDate.getDay() + 1);
      break;
    }
    case Granularity.Month: {
      startDate.setDate(1);
      break;
    }
  }
  const date = new Date(startDate);
  while (date <= endDate) {
    range.push(new Date(date));
    switch (granularity) {
      case Granularity.Day: {
        date.setDate(date.getDate() + 1);
        break;
      }
      case Granularity.Week: {
        date.setDate(date.getDate() + 7);
        break;
      }
      case Granularity.Month: {
        date.setMonth(date.getMonth() + 1);
        break;
      }
      default: {
        console.warn(`granularity default case encountered: ${granularity}`);
        date.setDate(date.getDate() + 1);
        return;
      }
    }
  }
  return range;
}

// getGranularityForRange matches the granularity algorithm in multiloc analytics
export function getGranularityForRange(start: Date, end: Date): Granularity {
  const numMonths = dayjs(end).diff(dayjs(start), 'months');
  if (numMonths >= 8) {
    return Granularity.Month;
  } else if (numMonths >= 2) {
    return Granularity.Week;
  } else {
    return Granularity.Day;
  }
}

// getUTCDate gets the UTC date
export function getUTCDate(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

// getAlignmentPeriodForRange chooses an alignment period to get a reasonable amount of rows assuming there is data throughout the period
export function getAlignmentPeriodForRange(start: Date, end: Date): AlignmentPeriodCalendar {
  return getAlignmentPeriodForMomentRange(dayjs(start), dayjs(end));
}

// getPreviousPeriod returns the previous period date range given a date range and the time range option
export function getPreviousPeriod(startDate: Date, endDate: Date, isMonthlyReport: boolean): [Date, Date] {
  let prevRange: [dayjs.Dayjs, dayjs.Dayjs];
  if (isMonthlyReport) {
    prevRange = previousMonthMoment([dayjs.utc(startDate), dayjs.utc(endDate)]);
  } else {
    prevRange = previousPeriodMoment([dayjs.utc(startDate), dayjs.utc(endDate)]);
  }
  return [prevRange[0].toDate(), prevRange[1].toDate()];
}

// getAlignmentPeriodForMomentRange chooses an alignment period to get a reasonable amount of rows
// assuming there is data throughout the period
export function getAlignmentPeriodForMomentRange(start: dayjs.Dayjs, end: dayjs.Dayjs): AlignmentPeriodCalendar {
  const numMonths = end.diff(start, 'months');
  if (numMonths >= 8) {
    return AlignmentPeriodCalendar.CALENDAR_MONTH;
  } else if (numMonths >= 2) {
    return AlignmentPeriodCalendar.CALENDAR_WEEK;
  } else {
    return AlignmentPeriodCalendar.CALENDAR_DAY;
  }
}

// getFormattingOption determines the alignment period and then maps that to a formatting key for charts
const formatMap = {};
formatMap[AlignmentPeriodCalendar.CALENDAR_DAY] = 'day';
formatMap[AlignmentPeriodCalendar.CALENDAR_WEEK] = 'week';
formatMap[AlignmentPeriodCalendar.CALENDAR_MONTH] = 'month';

export function getFormattingOption(start: Date, end: Date): FormattingOptions {
  const alignment = getAlignmentPeriodForRange(start, end);
  return formatMap[alignment] || 'day';
}

// timeRangeText returns the representation of a time range defined by start end boundaries
export function timeRangeText(dStr: string | Date, d2Str: string | Date, g: Granularity): string {
  // Periods starting on dates
  const d = dayjs.utc(dStr);
  const d2 = dayjs.utc(d2Str);
  // Find template to represent period using start date
  let t: string;
  if (g === Granularity.Day) {
    if (d.year() !== d2.year()) {
      t = 'MMM D, YYYY';
    } else {
      t = 'MMM D';
    }

    const thisDay = d.format(t);
    const otherDay = d2.format(t);

    return `${thisDay} vs ${otherDay}`;
  } else if (g === Granularity.Week) {
    if (d.year() !== d2.year()) {
      t = 'MMM D, YYYY';
    } else {
      t = 'MMM D';
    }

    const thisWeek = d.format(t);
    const otherWeek = d2.format(t);

    return `Weeks of ${thisWeek} vs ${otherWeek}`;
  } else if (g === Granularity.Month) {
    t = 'MMM YYYY';
    const thisMonth = d.format(t);
    const otherMonth = d2.format(t);

    return `${thisMonth} vs ${otherMonth}`;
  }
}

// Returns the previous period date range given a date range and the time range option
export function previousPeriod(dr: [Date, Date]): [Date, Date] {
  // Find the duration in days between end and start
  const s = dayjs(dr[0]);
  const e = dayjs(dr[1]);
  const diff = e.diff(s, 'days', true);
  // Offset both dates by that many days
  const sDate = dayjs(s).subtract(diff, 'day').toDate();
  const eDate = dayjs(e).subtract(diff, 'day').toDate();
  return [sDate, eDate];
}

// Returns the previous period date range given a date range and the time range option
// Uses dayjs inputs to avoid assuming timezones
export function previousPeriodMoment(dr: [dayjs.Dayjs, dayjs.Dayjs]): [dayjs.Dayjs, dayjs.Dayjs] {
  // Find the duration in days between end and start
  const s = dr[0];
  const e = dr[1];

  const diff = e.diff(s, 'days', true);
  const sOut = s.clone().subtract(diff, 'day');
  const eOut = e.clone().subtract(diff, 'day');
  return [sOut, eOut];
}

// Converts the passed in date to the previous version of the month.
export function previousMonthMoment(dr: [dayjs.Dayjs, dayjs.Dayjs]): [dayjs.Dayjs, dayjs.Dayjs] {
  const middleDate = dr[0].utc().date(15).subtract(1, 'month');
  const start = middleDate.utc().startOf('month');
  const end = middleDate.utc().endOf('month');

  return [start, end];
}

// GMB data is 5 days delay, if last 5 days included in selected date range,
// then return 5 days offsets.
export function GMBPeriodMoment(endDate: dayjs.Dayjs): dayjs.Dayjs {
  const e = endDate;
  if (e >= dayjs.utc(dayjs().format('YYYY-MM-DD'))) {
    return dayjs.utc(e.clone()).subtract(5, 'days');
  }
  return e;
}

// GBPKeywordPreviousPeriodMoment returns the previous period date range given a date range and the time range option
// and sets the start date to the first day of the month
export function GBPKeywordPreviousPeriodMoment(dr: [dayjs.Dayjs, dayjs.Dayjs]): [dayjs.Dayjs, dayjs.Dayjs] {
  // Find the duration in days between end and start
  const s = dr[0];
  const e = dr[1];
  if (
    s.utc().month() === e.utc().month() &&
    s.utc().date() === e.utc().startOf('month').date() &&
    e.utc().date() === e.utc().endOf('month').date() &&
    s.utc().year() === e.utc().year()
  ) {
    // Need to do this weird month subtraction because dayjs doesn't consider the days
    // correctly when you subtract a day/month. e.g.
    // dayjs.utc('2021-07-31').subtract(1, 'month') will return May 31st.
    // Since there's no 31st in June, it will try to find the next valid date, which is May 31st.
    const prevMonth = s.utc().get('month') > 0 ? s.utc().get('month') - 1 : 11;
    const sOut = s.clone().utc().subtract(1, 'month').month(prevMonth);
    const eOut = e.clone().utc().subtract(1, 'month').month(prevMonth);

    return [sOut.startOf('month'), eOut.endOf('month')];
  }

  const [pSOut, pEOut] = previousPeriodMoment(dr);

  return [pSOut.utc().startOf('month'), pEOut];
}
